class ShopifyHeader extends HTMLElement {
  constructor() {
    super();
    this.header = this;
    this.lastScrollTop = 0;
    this.ticking = false;
  }

  connectedCallback() {
    this.menuType = this.dataset.menuType;
    this.stickyBehavior = this.dataset.stickyBehavior;
    this.mobileMenuType = this.dataset.mobileMenuType;
    this.submenuAction = this.dataset.submenuAction;
    this.setupStickyHeader();

    if (this.menuType === 'dropdown' || this.menuType === 'mega') {
      this.setupDesktopSubmenuBehavior();
    }

    this.setupDeferredNavImages();
    this.setupMenuDrawer();
    this.setupPredictiveSearch();
    this.setupAnnouncementBarClose();
    this.setupCartCountBadge();
    this.setupThemeEditor();
  }

  // Shows the menu images (.nav-deferred-image) the first time the visitor hovers, taps or tabs into the header
  setupDeferredNavImages() {
    if (!this.hasAttribute('data-nav-images-pending')) return;

    const events = ['pointerover', 'focusin', 'touchstart'];
    const reveal = () => {
      this.removeAttribute('data-nav-images-pending');
      events.forEach((type) => this.removeEventListener(type, reveal));
    };
    events.forEach((type) => this.addEventListener(type, reveal, { passive: true }));
  }

  setupStickyHeader() {
    this.headerHeight = this.header.offsetHeight;
    this.announcementBar = document.querySelector('.announcement-bar');
    this.announcementBarHeight = this.announcementBar ? this.announcementBar.offsetHeight : 0;
  
    document.documentElement.style.setProperty('--header-height', `${this.headerHeight}px`);
    document.documentElement.style.setProperty('--announcement-bar-height', `${this.announcementBarHeight}px`);
  
    if (this.stickyBehavior === 'always' || this.stickyBehavior === 'scroll_up') {
      window.addEventListener('scroll', this.onScroll.bind(this), false);
    }
  }

  onScroll() {
    if (!this.ticking) {
      window.requestAnimationFrame(() => {
        this.updateHeaderVisibility();
        this.ticking = false;
      });
      this.ticking = true;
    }
  }

  updateHeaderVisibility() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const headerBottom = this.header.getBoundingClientRect().bottom;
    const announcementBarBottom = this.announcementBar ? this.announcementBar.getBoundingClientRect().bottom : -1;

    if (scrollTop > this.announcementBarHeight && this.stickyBehavior === 'always') {
      this.header.classList.add('fixed', 'top-0');
      this.header.style.transform = 'translateY(0)';
    } else if (this.announcementBar && announcementBarBottom >= 0) {
      this.header.classList.remove('fixed', 'top-0');
      this.header.style.transform = '';
    }

    if (this.stickyBehavior === 'scroll_up') {
      if (scrollTop < this.lastScrollTop && headerBottom <= 0) {
        this.header.classList.add('fixed', 'top-0');
        this.header.style.transform = 'translateY(0)';
      } else if (scrollTop > this.lastScrollTop && scrollTop > this.headerHeight + this.announcementBarHeight) {
        this.header.style.transform = `translateY(-${this.headerHeight}px)`;
      }
    }

    this.lastScrollTop = scrollTop;
  }

  setupDesktopSubmenuBehavior() {
    // A top-level item with a panel (snippets/header-mega-menu.liquid) is open while it has
    // the "active-menu-link" class; the panel's visibility is driven by that class in CSS.
    const menuItems = Array.from(this.header.querySelectorAll('.mega-menu-item'));
    const panelItems = menuItems.filter(item => item.querySelector('[data-mega-panel]'));
    const isHover = this.submenuAction === 'hover';
    let openTimeout;
    let closeTimeout;

    this.activeMegaMenuItem = null;

    this.openMegaMenu = (item) => {
      clearTimeout(openTimeout);
      clearTimeout(closeTimeout);
      if (this.activeMegaMenuItem === item) return;
      if (this.activeMegaMenuItem) this.closeMegaMenu({ keepBackground: true });

      item.classList.add('active-menu-link');
      item.querySelector('[data-mega-trigger]')?.setAttribute('aria-expanded', 'true');
      this.activeMegaMenuItem = item;
      this.handleNavbarAnimation(true);
      this.triggerMegaMenuAnimation(item);
    };

    this.closeMegaMenu = ({ keepBackground = false } = {}) => {
      clearTimeout(openTimeout);
      clearTimeout(closeTimeout);
      const item = this.activeMegaMenuItem;
      if (!item) return;

      item.classList.remove('active-menu-link');
      item.querySelector('[data-mega-trigger]')?.setAttribute('aria-expanded', 'false');
      this.activeMegaMenuItem = null;
      if (!keepBackground) this.handleNavbarAnimation(false);
    };

    const scheduleClose = () => {
      clearTimeout(openTimeout);
      clearTimeout(closeTimeout);
      closeTimeout = setTimeout(() => this.closeMegaMenu(), 150);
    };

    panelItems.forEach(item => {
      const trigger = item.querySelector('[data-mega-trigger]');

      if (isHover) {
        item.addEventListener('mouseenter', () => {
          clearTimeout(closeTimeout);
          if (this.activeMegaMenuItem === item) return;
          clearTimeout(openTimeout);
          // Small delay so sweeping the cursor across the nav doesn't flash the panel
          openTimeout = setTimeout(() => this.openMegaMenu(item), this.activeMegaMenuItem ? 0 : 100);
        });
        item.addEventListener('mouseleave', scheduleClose);
      }

      trigger?.addEventListener('click', (event) => {
        if (this.activeMegaMenuItem !== item) {
          this.openMegaMenu(item);
          return;
        }
        // With hover, a mouse/touch click on an open menu keeps it open; keyboard clicks (detail 0) toggle
        if (isHover && event.detail !== 0) return;
        this.closeMegaMenu();
      });

      // Tabbing out of the item closes it
      item.addEventListener('focusout', (event) => {
        if (this.activeMegaMenuItem === item && event.relatedTarget && !item.contains(event.relatedTarget)) {
          this.closeMegaMenu();
        }
      });
    });

    // Hovering another top-level link closes the open panel
    menuItems
      .filter(item => !panelItems.includes(item))
      .forEach(item => {
        item.addEventListener('mouseenter', () => {
          if (this.activeMegaMenuItem) this.closeMegaMenu();
        });
      });

    document.addEventListener('click', (event) => {
      if (this.activeMegaMenuItem && !this.activeMegaMenuItem.contains(event.target)) {
        this.closeMegaMenu();
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !this.activeMegaMenuItem) return;
      const trigger = this.activeMegaMenuItem.querySelector('[data-mega-trigger]');
      this.closeMegaMenu();
      trigger?.focus();
    });
  }

  setupMenuDrawer() {
    const menuToggle = this.header.querySelector('#menu-toggle');
    const menuToggleInner = this.header.querySelector('#menu-toggle-inner');
    const menuDrawer = this.header.querySelector('#menu-drawer');
    const menuIcon = menuToggle.querySelector('.menu-icon');
    const closeIcon = menuToggle.querySelector('.close-icon');
    const overlay = this.header.querySelector('#drawer-overlay');

    this.setDrawerHeight();
    window.addEventListener('resize', this.setDrawerHeight.bind(this));
    window.addEventListener('scroll', this.setDrawerHeight.bind(this));

    menuToggle.addEventListener('click', () => {
      this.toggleDrawer(menuDrawer, menuIcon, closeIcon, overlay);
    });
    menuToggleInner.addEventListener('click', () => {
      this.toggleDrawer(menuDrawer, menuIcon, closeIcon, overlay);
    });

    overlay.addEventListener('click', () => {
      this.toggleDrawer(menuDrawer, menuIcon, closeIcon, overlay);
    });

    document.addEventListener('keydown', (event) => {
      if (!this.isDrawerOpen()) return;

      if (event.key === 'Escape') {
        this.toggleDrawer(menuDrawer, menuIcon, closeIcon, overlay);
      } else if (event.key === 'Tab') {
        this.trapFocus(event, menuDrawer);
      }
    });

    // Accordions (snippets/header-menu-drawer.liquid): the "open" attribute drives the CSS
    menuDrawer.querySelectorAll('[data-accordion-toggle]').forEach(button => {
      button.addEventListener('click', () => {
        const isOpen = button.closest('.details').toggleAttribute('open');
        button.setAttribute('aria-expanded', String(isOpen));
      });
    });

    if (this.mobileMenuType !== 'accordion') {
      this.setupNewPageInDrawer(menuDrawer);
    }
  }

  isDrawerOpen() {
    const menuDrawer = this.header.querySelector('#menu-drawer');
    return Boolean(menuDrawer) && !menuDrawer.classList.contains('-translate-x-full');
  }

  // Keeps Tab / Shift+Tab inside the open drawer (it's an aria-modal dialog)
  trapFocus(event, container) {
    const focusable = Array.from(
      container.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), [tabindex]:not([tabindex="-1"])')
    ).filter(el => (el.checkVisibility ? el.checkVisibility({ checkVisibilityCSS: true, visibilityProperty: true }) : el.offsetParent !== null));
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (!container.contains(document.activeElement)) {
      event.preventDefault();
      first.focus();
    } else if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  setDrawerHeight() {
    const headerHeight = this.header.offsetHeight;
    const announcementBar = document.querySelector('.announcement-bar');
    let announcementBarVisibleHeight = 0;
  
    if (announcementBar) {
      const announcementBarRect = announcementBar.getBoundingClientRect();
      announcementBarVisibleHeight = Math.max(0, announcementBarRect.height - Math.abs(announcementBarRect.top));
    }
  
    const totalHeight = headerHeight + announcementBarVisibleHeight - headerHeight;
    const menuDrawer = this.header.querySelector('#menu-drawer');
    const menuDrawerOverlay = this.header.querySelector('#drawer-overlay');
    menuDrawer.style.height = `calc(100dvh - ${totalHeight}px)`;
    menuDrawer.style.top = `${totalHeight}px`;
    menuDrawerOverlay.style.height = `calc(100dvh - ${totalHeight}px)`;
    menuDrawerOverlay.style.top = `${totalHeight}px`;
  }

  toggleDrawer(menuDrawer, menuIcon, closeIcon, overlay) {
    const isOpening = menuDrawer.classList.contains('-translate-x-full');
    const menuToggle = this.header.querySelector('#menu-toggle');

    menuDrawer.classList.toggle('-translate-x-full');
    menuIcon.classList.toggle('hidden');
    closeIcon.classList.toggle('hidden');
    document.body.classList.toggle('overflow-hidden');
    overlay.classList.toggle('hidden');

    // The closed drawer is inert so its links stay out of the tab order
    menuDrawer.toggleAttribute('inert', !isOpening);
    menuToggle?.setAttribute('aria-expanded', String(isOpening));

    if (isOpening) {
      this.header.querySelector('#menu-toggle-inner')?.focus({ preventScroll: true });
    } else if (menuDrawer.contains(document.activeElement)) {
      menuToggle?.focus({ preventScroll: true });
    }
  }

  setupNewPageInDrawer(menuDrawer) {
    const submenuTriggers = menuDrawer.querySelectorAll('.submenu-trigger');
    submenuTriggers.forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        const submenuId = e.currentTarget.dataset.submenu;
        const submenu = this.header.querySelector(`#submenu-${submenuId}`);
        submenu?.classList.remove('hidden');
      });
    });

    const submenuBackButtons = menuDrawer.querySelectorAll('.submenu-back');
    submenuBackButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.currentTarget.closest('.submenu').classList.add('hidden');
      });
    });
  }

  handleNavbarAnimation(searchOpen) {
    // Cache DOM elements
    const newHeader = document.querySelector('.new-header');

    // Rendered by Liquid from the header settings
    const isAnimatedPage = newHeader?.dataset.transparent === 'true';
  
    // Check if the page is scrolled beyond a certain threshold
    const isScrolled = window.scrollY > 50;
    
    // Check if any mega menus are currently active
    const anyActiveMenus = this.header.querySelectorAll('.mega-menu-item.active-menu-link').length > 0;
  
    // Non-transparent pages stay in the solid state no matter what
    if (!isAnimatedPage) return;

    // show-bg drives the background, the text colour and the logo swap in CSS
    if (searchOpen || anyActiveMenus) {
      newHeader.classList.add('show-bg');
    } else if (!isScrolled) {
      newHeader.classList.remove('show-bg');
    }
  }

  triggerMegaMenuAnimation(menuItem) {
    const targetElements = menuItem.querySelectorAll('[data-mega-panel] .mega-menu-link');
    if (!window.anime || targetElements.length === 0) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    // Restart from the hidden state, then stagger the columns in while the panel fades in
    anime.remove(targetElements);
    targetElements.forEach(el => {
      el.style.opacity = '0';
      el.style.transform = 'translateY(16px)';
    });

    anime({
      targets: targetElements,
      translateY: [16, 0],
      opacity: [0, 1],
      easing: 'cubicBezier(0, 0, 0.3, 1)',
      duration: 400,
      delay: anime.stagger(50, { start: 150 }),
    });
  }
  
  
  setupPredictiveSearch() {
    const searchToggle = this.header.querySelector('#search-toggle');
    const searchOverlay = this.header.querySelector('#searchOverlay');
    const resultsWrapper = this.header.querySelector('#search-results-wrapper');
    const predictiveSearch = this.header.querySelector('#predictive-search');
    const searchClose = this.header.querySelector('#search-close');
    const searchInput = this.header.querySelector('#search-input');
    const searchResults = this.header.querySelector('#search-results');
    const searchClear = this.header.querySelector('#search-clear');

    if (!searchToggle) return;

    searchToggle.addEventListener('click', () => {
      predictiveSearch.classList.remove('-translate-y-[250%]', 'opacity-0');
      searchOverlay.classList.remove('invisible');
      this.header.classList.add('show-bg')
      document.body.classList.add('overflow-hidden');
      searchInput.focus();
      this.handleNavbarAnimation(true)
    });
  
    searchClose.addEventListener('click', () => {
      predictiveSearch.classList.add('-translate-y-[250%]', 'opacity-0');
      searchOverlay.classList.add('invisible');
      resultsWrapper.classList.add('hidden')
      searchInput.value = '';
      searchResults.innerHTML = '';
      document.body.classList.remove('overflow-hidden');
      this.handleNavbarAnimation(false)
    });

    searchOverlay.addEventListener('click', () => {
      predictiveSearch.classList.add('-translate-y-[250%]', 'opacity-0');
      searchOverlay.classList.add('invisible');
      resultsWrapper.classList.add('hidden')
      searchInput.value = '';
      searchResults.innerHTML = '';
      document.body.classList.remove('overflow-hidden');
      this.handleNavbarAnimation(false)

    });

    searchClear.addEventListener('click', () => {
      searchInput.value = '';
      searchResults.innerHTML = '';
      // searchClear.classList.add('hidden');
      searchInput.focus();
    });

    const debounce = (func, wait) => {
      let timeout;
      return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(this, args), wait);
      };
    };

    searchInput.addEventListener('input', debounce(async (e) => {
      const query = e.target.value.trim();
  
      if (query.length > 2) {
        // searchClear.classList.remove('hidden');
  
        try {
          const response = await fetch(`/search/suggest?q=${query}&section_id=predictive-search`);
          // const response1 = await fetch(`/search/suggest.json?q=${query}`);
          // const data = await response1.json();
          // console.log(data)
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const text = await response.text();
          const parser = new DOMParser();
          const parsedDocument = parser.parseFromString(text, 'text/html');

          searchResults.parentElement.classList.remove('hidden');
          searchResults.parentElement.classList.add('flex');
  
          const resultsMarkup = parsedDocument.querySelector('#predictive-search-results')?.innerHTML || '';
          searchResults.innerHTML = resultsMarkup;

        } catch (error) {
          console.error('Error fetching search results:', error);
        }
      } else {
        searchResults.innerHTML = '';
        // searchClear.classList.add('hidden');
      }
    }, 300));
  }
  
  setupCartCountBadge() {
    // cart-487.js rewrites every [data-cart-item-count]; hide the icon badge while the bag is empty
    this.header.querySelectorAll('[data-cart-count-badge]').forEach(badge => {
      const count = badge.querySelector('[data-cart-item-count]');
      if (!count) return;

      const sync = () => badge.classList.toggle('hidden', !(parseInt(count.textContent, 10) > 0));
      sync();
      new MutationObserver(sync).observe(count, { childList: true, characterData: true, subtree: true });
    });
  }

  setupThemeEditor() {
    if (!window.Shopify?.designMode) return;

    const desktop = window.matchMedia('(min-width: 1280px)');

    // Selecting a menu block in the theme editor opens the menu that shows it
    document.addEventListener('shopify:block:select', (event) => {
      if (!this.contains(event.target)) return;

      if (desktop.matches) {
        const item = event.target.closest('.mega-menu-item')
          || this.querySelector('[data-mega-panel][data-shopify-editor-block]')?.closest('.mega-menu-item');
        if (item && this.openMegaMenu) this.openMegaMenu(item);
        return;
      }

      if (!this.isDrawerOpen()) this.header.querySelector('#menu-toggle')?.click();
      const blockId = event.detail?.blockId;
      const target = blockId && this.querySelector(`#menu-drawer [data-shopify-editor-block*="${blockId}"]`);
      if (!target) return;

      const details = target.closest('.details');
      if (details && !details.hasAttribute('open')) details.querySelector('[data-accordion-toggle]')?.click();
      target.scrollIntoView({ block: 'center' });
    });

    document.addEventListener('shopify:block:deselect', (event) => {
      if (this.contains(event.target) && this.closeMegaMenu) this.closeMegaMenu();
    });
  }

  setupAnnouncementBarClose() {
    const announcementCloseButton = this.announcementBar?.querySelector('#announcement-close');
    if (announcementCloseButton) {
      announcementCloseButton.addEventListener('click', () => {
        this.announcementBar.classList.add('hidden');
        this.announcementBar.classList.remove('announcement-bar');
        this.setDrawerHeight();
        this.setupStickyHeader();
      });
    }
  }
}

customElements.define('header-487', ShopifyHeader);